/**
 * Per-(ticker, side) front-of-book depth velocity. Sibling of
 * priceVelocity.ts — same in-memory ring-buffer pattern, different
 * sample shape. Tracks contracts resting at the best bid for each
 * (ticker, side) the dashboard currently shows, then exposes the
 * signed depth delta over a 1-minute window so the moneyline +
 * spread/total grids can render a "K Δ/min" + "P Δ/min" column.
 *
 * Reading: positive Δ = depth building (more contracts being posted at
 * the best bid); negative Δ = depth draining (contracts being pulled or
 * hit). The most-cited use case for the paired K↔P columns is cross-
 * exchange sweep detection — if K depth tanks while P stays flat, P is
 * likely to follow shortly.
 *
 * Per-side: keyed by `${ticker}|${side}` because the existing K Q
 * column already filters by the toolbar's activeMarketSide, and the
 * velocity needs to match that semantic so traders read both numbers
 * consistently ("281k resting now, draining at 8k/min").
 *
 * Polymarket: wired symmetrically to Kalshi — both venues sample at
 * orderbook ingest. Unlike priceVelocity (which skipped Poly because
 * marketStore didn't surface poly price on the card VM), depth IS
 * available on every Poly VenueBook so there's no gap.
 */

interface DepthSample {
  t: number;
  /** Contracts at the best bid on this side. Integer; sub-contract
   *  values are an upstream bug. */
  depth: number;
}

const HISTORY = new Map<string, DepthSample[]>();
const LAST_APPEND_AT = new Map<string, number>();
// 10m max retention — matches priceVelocity. Leaves headroom for a
// future 5m window if we extend, and lets a trader who tab-switched
// for a minute see a fresh 1m chip almost immediately on return.
const MAX_AGE_MS = 10 * 60 * 1000;
// 2s dedup gap — depth ticks slower than price (book frames arrive at
// venue cadence, not per-quote). 2s matches orderVelocity's gap; goes
// to 1s like priceVelocity didn't seem to add signal in early traces.
const MIN_GAP_MS = 2000;

/** Record the current best-bid depth for a (ticker, side). Call from
 *  the orderbook ingest path so each new frame contributes a sample.
 *  MIN_GAP_MS dedup collapses rapid back-to-back frames into a single
 *  bucket. */
export function recordDepthSample(key: string, depth: number): void {
  if (!Number.isFinite(depth) || depth < 0) return;
  const rounded = Math.round(depth);
  const now = Date.now();
  let arr = HISTORY.get(key);
  if (!arr) {
    arr = [];
    HISTORY.set(key, arr);
  }
  const last = arr[arr.length - 1];
  const lastAppendAt = LAST_APPEND_AT.get(key);
  if (last && lastAppendAt != null && now - lastAppendAt < MIN_GAP_MS) {
    last.t = now;
    last.depth = rounded;
  } else {
    arr.push({ t: now, depth: rounded });
    LAST_APPEND_AT.set(key, now);
  }
  const cutoff = now - MAX_AGE_MS;
  while (arr.length > 1 && arr[0].t < cutoff) arr.shift();
}

export interface DepthVelocityWindow {
  /** Contracts/min delta over the window — equivalent to "depth now
   *  minus depth at window start". Positive = building, negative =
   *  draining. Null until history reaches back across the window. */
  depthDelta: number | null;
}

const EMPTY: DepthVelocityWindow = { depthDelta: null };

function deltaOver(arr: DepthSample[], windowMs: number): DepthVelocityWindow {
  const cur = arr[arr.length - 1];
  const target = Date.now() - windowMs;
  let base: DepthSample | null = null;
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    if (arr[i].t <= target) {
      base = arr[i];
      break;
    }
  }
  if (!base) return EMPTY;
  return { depthDelta: cur.depth - base.depth };
}

export interface BookDepthVelocity {
  /** 1-minute trailing depth delta — the primary read for "is the
   *  front of book filling up or draining out right now". */
  m1: DepthVelocityWindow;
}

/** Depth velocity for a (ticker, side) key over the 1m window. Returns
 *  EMPTY until enough history has accumulated to span the window. */
export function bookDepthVelocity(key: string): BookDepthVelocity {
  const arr = HISTORY.get(key);
  if (!arr || arr.length < 2) return { m1: EMPTY };
  return {
    m1: deltaOver(arr, 60_000),
  };
}

/** A sample stream whose newest sample is older than this has stopped, and a
 *  stalled stream must degrade to `unavailable`: computing the 1m delta from
 *  old samples would freeze a stale signed value (or a false `0`) on screen
 *  exactly when the data went dark (#665, Grim's sustained-blank class).
 *
 *  Bound choice — aligned with the repository's feed-staleness contract, not
 *  invented: useFeedFreshness deliberately rejects aggressive per-market
 *  staleness ("quiet is NOT stale" — a healthy pre-game book can go long
 *  stretches between orderbook frames without being broken; its
 *  fault line is TRANSPORT_STALL_MS = 65s). 65s is also this metric's own
 *  semantic boundary: a 1-minute delta whose NEWEST evidence is older than
 *  the window itself contains zero in-window evidence. Quiet-but-broadcasting
 *  books never trip this — MIN_GAP_MS dedup refreshes the newest sample's
 *  timestamp on every arriving frame even when depth is unchanged. */
export const SAMPLE_STALE_MS = 65_000;

/** #665 — the depth-velocity display lifecycle. One glyph used to mean both
 *  "collecting the first minute of samples" and "no data": callers now get
 *  the distinction without any new polling or subscription work.
 *
 *  - `unavailable`: no key/book, no samples at all, or the sample stream has
 *    stalled past SAMPLE_STALE_MS. Genuinely no trustworthy evidence — and
 *    the honest answer for a market whose orderbook frames simply never
 *    arrive (a ticker outside the orderbook-needs union, or a feed that does
 *    not carry this book). Renders `—` as before.
 *  - `warming`: live samples exist but history does not yet reach back one
 *    minute. A healthy board in its first minute.
 *  - `ready`: the window is spanned; `delta` is the signed value, including
 *    a truthful 0.
 *
 *  Identity resets come for free: pruneDepthVelocity / ticker replacement
 *  drop the key's history, so the next read is `unavailable` → `warming`
 *  with no cross-identity inheritance. */
export type DepthVelocityDisplay =
  | { kind: 'ready'; delta: number }
  | { kind: 'warming' }
  | { kind: 'unavailable' };

export function depthVelocityDisplay(key: string | null): DepthVelocityDisplay {
  if (key == null) return { kind: 'unavailable' };
  const arr = HISTORY.get(key);
  if (!arr || arr.length === 0) return { kind: 'unavailable' };
  const newest = arr[arr.length - 1];
  if (Date.now() - newest.t > SAMPLE_STALE_MS) return { kind: 'unavailable' };
  const { depthDelta } = deltaOver(arr, 60_000);
  return depthDelta == null ? { kind: 'warming' } : { kind: 'ready', delta: depthDelta };
}

/** Accessible explanation for the warm-up glyph — shared by every Δ/m cell so
 *  the tooltip cannot drift between surfaces. */
export const DEPTH_WARMUP_LABEL = 'Collecting one minute of depth history';

/** Compose the per-side key used by recordDepthSample / bookDepthVelocity.
 *  Centralized so callers can't drift on the separator and miss samples
 *  silently. */
export function depthKey(ticker: string, side: 'yes' | 'no'): string {
  return `${ticker}|${side}`;
}

/** Resolve a display-oriented VenueBook side back to the venue-native history
 * key. Polymarket binary moneylines project one native book into two team rows;
 * the complement row swaps YES/NO for display, so using its display side
 * directly made both rows read the same velocity series (#582). */
export function depthKeyForBook(
  book: { marketTicker: string; nativeSideInverted?: boolean },
  side: 'yes' | 'no',
): string {
  const nativeSide = book.nativeSideInverted
    ? side === 'yes' ? 'no' : 'yes'
    : side;
  return depthKey(book.marketTicker, nativeSide);
}

/** Drop history for (ticker, side) keys no longer rendered. Call from
 *  the same effect that records samples, with the active set. */
export function pruneDepthVelocity(activeKeys: Set<string>): void {
  for (const key of HISTORY.keys()) {
    if (!activeKeys.has(key)) {
      HISTORY.delete(key);
      LAST_APPEND_AT.delete(key);
    }
  }
}

/** Test-only: clear all history. Use in afterEach so per-test samples
 *  don't bleed into siblings. */
export function __resetDepthVelocityForTests(): void {
  HISTORY.clear();
  LAST_APPEND_AT.clear();
}

/** Format a signed depth delta into a compact display string. Mirrors
 *  formatVolume's M / K compaction but keeps an explicit + or − sign so
 *  the K Δ/m and P Δ/m cells read at a glance. Zero renders as "0"
 *  (not "—") so a steady book doesn't visually drop out. */
export function formatSignedVolume(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  const sign = n > 0 ? '+' : '−';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${Math.round(abs)}`;
}

/** Tailwind color class for a signed depth delta. Positive = building
 *  (green tone matching queueTone's "calm" bucket); negative =
 *  draining (red tone matching queueTone's "exhausted" bucket); zero
 *  or null = muted neutral. Kept here so the K Δ/m + P Δ/m cells in
 *  GameTable and SpreadsTotalsTable read identically. */
export function depthDeltaTone(n: number | null): string {
  if (n == null || n === 0) return 'text-muted-foreground';
  if (n > 0) return 'text-positive';
  return 'text-negative';
}
